import { useCallback, useEffect, useState } from 'react';
import { getUserProfile } from '@leagueplatform/user-profile-api';
import type { UserProfileAggregate } from '@leagueplatform/user-profile-api';
import { SocketAsFetch } from '@leagueplatform/socket-as-fetch';

export const useOnboarding = () => {
  const [userProfile, setUserProfile] = useState<UserProfileAggregate | null>(
    null,
  );
  const [shouldShowAccountSetup, setShouldShowAccountSetup] = useState(false);
  const [shouldShowFeatureHighlights, setShouldShowFeatureHighlights] =
    useState(false);
  const [shouldShowAllSet, setShouldShowAllSet] = useState(false);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    getUserProfile()
      .then((profile) => {
        setUserProfile(profile);
        // Both flags are computed from this single load, independent of one
        // another — PrivateRoutesContainer decides render priority (Account
        // Setup, then Feature Highlights, then All Set). Computing both up
        // front (rather than short-circuiting) lets `onAccountSetupComplete`
        // fall through to the already-known Feature Highlights state
        // without a second fetch.
        setShouldShowAccountSetup(
          !profile.member_post_ftux?.account_setup_complete,
        );
        setShouldShowFeatureHighlights(!profile.ftux_complete);
      })
      .catch(() => {
        // On error, default to not showing onboarding
      })
      .finally(() => setLoading(false));
  }, []);

  const onAccountSetupComplete = useCallback(() => {
    // Hide the screen immediately rather than waiting on the fetch below —
    // otherwise a second tap on Continue before it resolves re-submits.
    setShouldShowAccountSetup(false);
    SocketAsFetch.fetch({
      message_type: 'set_post_ftux',
      info: { account_setup_complete: true },
    }).catch(() => {});
  }, []);

  const onOnboardingComplete = useCallback((error?: Error) => {
    if (!error) {
      setShouldShowAllSet(true);
    }
    setShouldShowFeatureHighlights(false);
  }, []);

  const onAllSetComplete = useCallback(() => {
    SocketAsFetch.fetch({
      message_type: 'set_post_ftux',
      info: { ftux_complete: true },
    })
      .catch(() => {})
      .finally(() => setShouldShowAllSet(false));
  }, []);

  return {
    userProfile,
    shouldShowAccountSetup,
    shouldShowFeatureHighlights,
    shouldShowAllSet,
    isLoading,
    onAccountSetupComplete,
    onOnboardingComplete,
    onAllSetComplete,
  };
};
